/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  RouteScoreResponse,
  SafeHaven,
  SafetyMode,
} from "@/types/safecircle";
import { migrateLegacyModeValue } from "@/utils/safecircle/migrateMode";
import { addDismissedUid } from "@/utils/safecircle/mockFriendDismissal";

interface SafeCircleStore {
  // Session 1 — active profile selector
  activeProfileId: string | null;
  setActiveProfile: (id: string | null) => void;

  // Session 2 — scored routes + Safe Haven selection
  mode: SafetyMode;
  setMode: (mode: SafetyMode) => void;

  lastRouteResult: RouteScoreResponse | null;
  setLastRouteResult: (r: RouteScoreResponse | null) => void;

  selectedHaven: SafeHaven | null;
  setSelectedHaven: (h: SafeHaven | null) => void;

  // Build 93 — pre-loaded MockFriend dismissal. Flipped once the user
  // removes MockFriend from a populated circle (only possible after
  // they have ≥1 real member). Persisted so a tab away + back doesn't
  // re-surface the example.
  //
  // Build 164 — scoped PER-ACCOUNT (uid set) instead of one device-wide
  // boolean. The old boolean suppressed MockFriend for every later account
  // on the same device; now dismissal only hides it for the uid that
  // dismissed it. Read via isMockDismissedForUid(dismissedMockForUids, uid).
  dismissedMockForUids: string[];
  dismissMockForUid: (uid: string | null | undefined) => void;
}

export const useSafeCircleStore = create<SafeCircleStore>()(
  persist(
    (set) => ({
      activeProfileId: null,
      setActiveProfile: (id) => set({ activeProfileId: id }),

      mode: "private",
      setMode: (mode) => set({ mode }),
      lastRouteResult: null,
      setLastRouteResult: (r) => set({ lastRouteResult: r }),
      selectedHaven: null,
      setSelectedHaven: (h) => set({ selectedHaven: h }),

      dismissedMockForUids: [],
      dismissMockForUid: (uid) =>
        set((s) => ({
          dismissedMockForUids: addDismissedUid(s.dismissedMockForUids, uid),
        })),
    }),
    {
      name: "myambits.safeCircle",
      version: 2,
      migrate: (state: unknown, version: number) => {
        const s = (state && typeof state === "object" ? state : {}) as {
          mode?: string;
          mockFriendDismissed?: boolean;
          dismissedMockForUids?: string[];
        };
        // v1 → v2: the device-wide boolean was too sticky (suppressed
        // MockFriend for every later account on the device). Drop it →
        // empty per-uid set, so previously-suppressed accounts see the
        // onboarding example once more and can re-dismiss per-account.
        const next: Record<string, unknown> = { ...s };
        if (version < 2) {
          delete next.mockFriendDismissed;
          next.dismissedMockForUids = [];
        }
        const migratedMode = migrateLegacyModeValue(s.mode);
        if (migratedMode && migratedMode !== s.mode) {
          next.mode = migratedMode;
        }
        return next as unknown as SafeCircleStore;
      },
      partialize: (s) => ({
        activeProfileId: s.activeProfileId,
        mode: s.mode,
        dismissedMockForUids: s.dismissedMockForUids,
      }),
    },
  ),
);
